import { ogImageMeta, hubOgKey, bestOgKey } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchCategoryHub } from "@/lib/library-hubs.functions";
import { categoryIntro, HUB_MIN_ITEMS } from "@/lib/library-hubs";
import {
  LibraryHubPage,
  itemListJsonLd,
  breadcrumbJsonLd,
  datasetJsonLd,
} from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/library/c/$category")({
  loader: async ({ params }) => {
    try {
      const hub = await fetchCategoryHub({ data: { category: params.category } });
      return { hub };
    } catch {
      return { hub: null };
    }
  },
  head: ({ params, loaderData }) => {
    const hub = loaderData?.hub ?? null;
    const url = `https://vibing.inc/library/c/${params.category}`;
    if (!hub) {
      return {
        meta: [
          { title: "Collection not found — The Vibeary" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${hub.category} — ${hub.count} hand-picked resources | The Vibeary`;
    const desc = categoryIntro(hub.category, hub.count).slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...ogImageMeta(hubOgKey(params.category), `Share card: ${hub.category} collection in the Hidden Gems Library`),
        { name: "twitter:card", content: "summary_large_image" },
        ...(hub.count < HUB_MIN_ITEMS
          ? [{ name: "robots", content: "noindex,follow" }]
          : []),
      ],
      links: [
        { rel: "canonical", href: url },
        {
          rel: "alternate",
          type: "text/markdown",
          href: `https://vibing.inc/md/library/c/${params.category}`,
          title: "Markdown",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: itemListJsonLd(hub.category, url, hub.gems),
        },
        {
          type: "application/ld+json",
          children: datasetJsonLd({
            name: `${hub.category} — The Vibing Library`,
            description: desc,
            url,
            count: hub.count,
            keywords: [hub.category, "tools", "resources", "curated"],
          }),
        },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", item: "https://vibing.inc/" },
            { name: "The Vibeary", item: "https://vibing.inc/library" },
            { name: hub.category, item: url },
          ]),
        },
      ],
    };
  },
  component: CategoryHub,
});

function CategoryHub() {
  const { hub } = Route.useLoaderData();
  const { category } = Route.useParams();

  if (!hub) {
    return (
      <div className="min-h-dvh bg-background text-foreground">
        <main id="main" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Collection not found</h1>
          <Link to="/library" className="mt-4 inline-block text-muted-foreground underline">
            Back to the library
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <LibraryHubPage
      eyebrow="The Vibeary — collection"
      title={`${hub.category}`}
      intro={categoryIntro(hub.category, hub.count)}
      gems={hub.gems}
      crumbs={[
        { label: "Home", to: "/" },
        { label: "The Vibeary", to: "/library" },
        { label: hub.category },
      ]}
      relatedTitle={`Inside ${hub.category}`}
      related={hub.siblings
        .filter((s) => s.count >= HUB_MIN_ITEMS)
        .map((s) => ({
          label: s.name,
          href: `/library/c/${category}/${s.slug}`,
          count: s.count,
        }))}
    />
  );
}
